import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { WikiDocumentStatus, WikiExternalSyncLog } from '@innermate/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useEffect, useState } from 'react';

const KIND_LABEL: Record<string, string> = {
  file: '파일',
  text: '텍스트',
  delete: '삭제',
};

type StatusFilter = 'all' | 'success' | 'failed';

export function AdminWikiExternalSyncLogs() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const categoriesQuery = useQuery({
    queryKey: ['wiki', 'categories'],
    queryFn: () => api.wikiCategories(),
    staleTime: 60_000,
  });

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['admin', 'wikis', 'external-sync-logs', status, debouncedQ],
    queryFn: ({ pageParam }) =>
      api.adminWikiExternalSyncLogs({
        limit: 100,
        cursor: pageParam ?? undefined,
        status: status === 'all' ? undefined : status,
        q: debouncedQ || undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? null,
    refetchInterval: 5_000,
  });

  const logs = data?.pages.flatMap((p) => p.logs) ?? [];
  const firstPage = data?.pages[0];
  const totalCount = firstPage?.total ?? logs.length;
  const successCount = firstPage?.successTotal ?? 0;
  const failedCount = firstPage?.failedTotal ?? 0;

  return (
    <PageShell
      header={
        <PageHeader
          title="외부 sync 로그"
          description="wiki 등 외부 시스템의 sync 시도. 성공/실패 모두 기록."
        />
      }
    >
      <IngestConcurrencyControl />

      <div className="mb-4 flex items-center gap-2">
        <FilterChip
          active={status === 'all'}
          onClick={() => setStatus('all')}
          label={`전체 ${totalCount}`}
        />
        <FilterChip
          active={status === 'success'}
          onClick={() => setStatus('success')}
          label={`성공 ${successCount}`}
          tone="success"
        />
        <FilterChip
          active={status === 'failed'}
          onClick={() => setStatus('failed')}
          label={`실패 ${failedCount}`}
          tone="destructive"
        />
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted pointer-events-none" />
            <input
              type="text"
              placeholder="제목 또는 업로더 검색"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-9 w-56 pl-9 pr-3 text-sm rounded-md border border-border bg-surface focus:outline-none focus:border-accent"
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="h-9"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn('size-4', isFetching && 'animate-spin')} />
            새로고침
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '로그를 불러오지 못했습니다.'}
        </p>
      ) : logs.length === 0 ? (
        <p className="text-sm text-foreground-muted py-10 text-center">
          아직 sync 시도가 없습니다.
        </p>
      ) : (
        <div className="space-y-2">
          {logs.map((log) => (
            <LogRow
              key={log.id}
              log={log}
              categoryNameMap={Object.fromEntries(
                (categoriesQuery.data?.categories ?? []).map((c) => [c.slug, c.name]),
              )}
            />
          ))}
          {hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? '불러오는 중…' : `더 보기 (${logs.length}/${totalCount})`}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </PageShell>
  );
}

/**
 * wiki_config.ingestConcurrency 인라인 편집 — 외부 sync 가 한꺼번에 많이 들어왔을 때
 * 시스템 (워커) 의 LLM 호출 동시성을 즉시 조정할 수 있게 이 페이지에 둠. 사용자 LLM 슬롯은
 * 사용자별로 별도 제한되지만 wiki ingest 는 시스템 ID 로 무제한이라 부하 튀기 쉬움.
 *
 * 워커가 30초 TTL 캐시로 폴링하므로 저장 후 최대 30초 안에 적용됨.
 */
function IngestConcurrencyControl() {
  const qc = useQueryClient();
  const cfg = useQuery({
    queryKey: ['admin', 'wikis', 'config'],
    queryFn: () => api.adminWikiConfig(),
    staleTime: 30_000,
  });
  const [value, setValue] = useState<number | null>(null);
  useEffect(() => {
    if (cfg.data && value === null) setValue(cfg.data.ingestConcurrency);
  }, [cfg.data, value]);

  const update = useMutation({
    mutationFn: (n: number) => api.adminWikiConfigUpdate({ ingestConcurrency: n }),
    onSuccess: (next) => {
      qc.setQueryData(['admin', 'wikis', 'config'], next);
      setValue(next.ingestConcurrency);
    },
  });

  const current = cfg.data?.ingestConcurrency ?? null;
  const dirty = value !== null && current !== null && value !== current;
  const canSave = dirty && !update.isPending && value !== null && value >= 1 && value <= 20;

  return (
    <div className="mb-4 rounded-md border border-border bg-surface-muted/40 px-4 py-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex flex-col">
          <span className="text-sm font-medium">wiki 시스템 작업 동시 처리</span>
          <span className="text-xs text-foreground-muted">
            wiki.ingest (자료 정제) + wiki.curate-hubs (hub MOC 큐레이션) 등이 공유하는 동시성 cap.
            외부 sync 가 한꺼번에 들어오면 LLM 부하 튐 — 줄이면 안정성, 늘리면 처리량. 저장 후 최대
            30초 안에 워커에 반영.
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={20}
            value={value ?? ''}
            onChange={(e) => {
              const n = Number(e.target.value);
              setValue(Number.isFinite(n) ? n : null);
            }}
            disabled={cfg.isLoading || update.isPending}
            className="h-9 w-20 px-2 text-sm rounded-md border border-border bg-surface focus:outline-none focus:border-accent text-right"
          />
          <span className="text-xs text-foreground-muted">
            {current !== null ? `(현재 ${current})` : ''}
          </span>
          <Button
            variant={dirty ? 'default' : 'secondary'}
            size="sm"
            className="h-9"
            disabled={!canSave}
            onClick={() => value !== null && update.mutate(value)}
          >
            {update.isPending ? '저장 중…' : dirty ? '저장' : '저장됨'}
          </Button>
        </div>
      </div>
      {update.error ? (
        <p className="mt-2 text-xs text-destructive">
          {update.error instanceof ApiError ? update.error.message : '저장 실패'}
        </p>
      ) : null}
    </div>
  );
}

const DOC_STATUS_LABEL: Record<WikiDocumentStatus, string> = {
  uploaded: '대기 중',
  parsing: '변환 중',
  parsed: 'LLM 대기',
  digesting: 'LLM 정제 중',
  done: '완료',
  failed: '실패',
  deleted: '외부 삭제됨',
  archived: '보관됨',
};

const DOC_STATUS_TONE: Record<WikiDocumentStatus, string> = {
  uploaded: 'text-foreground-muted',
  parsing: 'text-amber-600 dark:text-amber-400',
  parsed: 'text-amber-600 dark:text-amber-400',
  digesting: 'text-accent',
  done: 'text-emerald-600 dark:text-emerald-400',
  failed: 'text-destructive',
  deleted: 'text-foreground-faint',
  archived: 'text-foreground-muted',
};

function DocStatusBadge({
  status,
  digestSummary,
}: {
  status: WikiDocumentStatus;
  digestSummary: import('@innermate/shared').WikiDigestSummary | null;
}) {
  const progress =
    status === 'digesting' && digestSummary?.totalChunks
      ? ` (${digestSummary.processedChunks ?? 0}/${digestSummary.totalChunks})`
      : '';
  return (
    <span className={`text-xs ${DOC_STATUS_TONE[status]}`}>
      ↳ {DOC_STATUS_LABEL[status]}
      {progress}
    </span>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  tone,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  tone?: 'success' | 'destructive';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 h-7 rounded-md text-xs border transition-colors',
        active
          ? tone === 'success'
            ? 'border-success/40 bg-success/15 text-success'
            : tone === 'destructive'
              ? 'border-destructive/40 bg-destructive/15 text-destructive'
              : 'border-accent/40 bg-accent/15 text-accent'
          : 'border-border bg-surface hover:bg-surface-muted text-foreground-muted',
      )}
    >
      {label}
    </button>
  );
}

function LogRow({
  log,
  categoryNameMap,
}: { log: WikiExternalSyncLog; categoryNameMap: Record<string, string> }) {
  const [open, setOpen] = useState(false);
  const failed = log.status === 'failed';
  const isDelete = log.kind === 'delete';

  return (
    <div
      className={cn(
        'rounded-md border bg-surface',
        failed ? 'border-destructive/30' : 'border-border',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 flex items-start gap-3 text-left hover:bg-surface-muted/40"
      >
        <div className="mt-0.5">
          {failed ? (
            <AlertCircle className="size-4 text-destructive" />
          ) : (
            <CheckCircle2 className="size-4 text-success" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium truncate">{log.title ?? '(제목 없음)'}</span>
            <span className="text-xs text-foreground-muted shrink-0">
              · {KIND_LABEL[log.kind] ?? log.kind}
            </span>
            {log.categorySlug ? (
              <span className="text-xs px-1.5 h-4 inline-flex items-center rounded-sm bg-surface-muted text-foreground-muted shrink-0">
                {categoryNameMap[log.categorySlug] ?? log.categorySlug}
              </span>
            ) : null}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-foreground-muted">
            <span>{formatTime(log.createdAt)}</span>
            {log.uploaderEmail ? (
              <>
                <span>·</span>
                <span className="truncate">
                  작성자: {log.uploaderEmail}
                  {log.resolvedUser?.displayName ? ` (${log.resolvedUser.displayName})` : ''}
                </span>
              </>
            ) : null}
            {log.lastModifiedByEmail && log.lastModifiedByEmail !== log.uploaderEmail ? (
              <>
                <span>·</span>
                <span className="truncate">
                  수정자: {log.lastModifiedByEmail}
                  {log.resolvedModifier?.displayName
                    ? ` (${log.resolvedModifier.displayName})`
                    : ''}
                </span>
              </>
            ) : null}
          </div>
          {!failed && !isDelete ? (
            <div className="mt-0.5">
              {log.document?.status ? (
                <DocStatusBadge
                  status={log.document.status}
                  digestSummary={log.document.digestSummary ?? null}
                />
              ) : (
                <span className="text-xs text-foreground-faint">↳ 새 sync로 대체됨</span>
              )}
            </div>
          ) : null}
          {failed && log.errorMessage ? (
            <div className="mt-1 text-xs text-destructive truncate">
              {log.errorCode ? `[${log.errorCode}] ` : ''}
              {log.errorMessage}
            </div>
          ) : null}
        </div>
        <div className="shrink-0 mt-0.5">
          {open ? (
            <ChevronDown className="size-4 text-foreground-muted" />
          ) : (
            <ChevronRight className="size-4 text-foreground-muted" />
          )}
        </div>
      </button>
      {open ? (
        <div className="border-t border-border px-3 py-2 text-xs space-y-1 bg-surface-muted/30">
          <KV k="로그 ID" v={log.id} />
          <KV k="외부 ID" v={log.externalId ?? '(없음)'} />
          <KV
            k="자료"
            v={
              log.document ? (
                <span>
                  <span className="font-mono">{log.document.id}</span>
                  <span className="ml-2 text-foreground-muted">({log.document.originalName})</span>
                </span>
              ) : (
                '(없음)'
              )
            }
          />
          <KV k="종류" v={KIND_LABEL[log.kind] ?? log.kind} />
          <KV k="제목" v={log.title ?? '(없음)'} />
          <KV
            k="카테고리"
            v={
              log.categorySlug
                ? `${categoryNameMap[log.categorySlug] ?? log.categorySlug} (${log.categorySlug})`
                : '(없음)'
            }
          />
          <KV
            k="작성자"
            v={
              log.uploaderEmail
                ? `${log.uploaderEmail}${log.resolvedUser?.displayName ? ` (${log.resolvedUser.displayName})` : ' (매칭 실패 / admin 대체)'}`
                : '(없음)'
            }
          />
          <KV
            k="수정자"
            v={
              log.lastModifiedByEmail
                ? `${log.lastModifiedByEmail}${log.resolvedModifier?.displayName ? ` (${log.resolvedModifier.displayName})` : ' (매칭 실패)'}`
                : '(없음)'
            }
          />
          <KV k="결과" v={log.status === 'success' ? '성공' : '실패'} />
          {failed ? (
            <>
              <KV k="오류 코드" v={log.errorCode ?? '(없음)'} />
              <KV k="오류 내용" v={log.errorMessage ?? '(없음)'} />
            </>
          ) : null}
          <KV k="시각" v={new Date(log.createdAt).toLocaleString('ko-KR')} />
        </div>
      ) : null}
    </div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="text-foreground-muted shrink-0 w-28 font-mono">{k}</span>
      <span className="font-mono break-all">{v}</span>
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diff = now - d.getTime();
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}초 전`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  return d.toLocaleString('ko-KR');
}
